import { SOUND_EFFECT_ASSET_URLS } from "./soundEffectAssets";
import type { SoundEffectId } from "./soundEffects";

export type SoundEffectController = {
  play: (soundEffectId: SoundEffectId) => void;
  syncVolume: (volumePercent: number) => void;
  dispose: () => void;
};

type AudioContextConstructor = typeof AudioContext;

function getGainVolume(volumePercent: number): number {
  return Math.min(1, Math.max(0, volumePercent / 100));
}

function getAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === "undefined") {
    return null;
  }

  const extendedWindow = window as Window & {
    webkitAudioContext?: AudioContextConstructor;
  };

  return window.AudioContext ?? extendedWindow.webkitAudioContext ?? null;
}

function applyGain(gainNode: GainNode, audioContext: AudioContext, volume: number): void {
  const now = audioContext.currentTime;
  gainNode.gain.cancelScheduledValues(now);
  gainNode.gain.setValueAtTime(gainNode.gain.value, now);
  gainNode.gain.linearRampToValueAtTime(volume, now + 0.01);
}

function isDocumentHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

export function createSoundEffectController(): SoundEffectController {
  let audioContext: AudioContext | null = null;
  let gainNode: GainNode | null = null;
  let currentVolume = 0;
  const activeSources = new Set<AudioBufferSourceNode>();
  const bufferPromises = new Map<SoundEffectId, Promise<AudioBuffer | null>>();

  const disconnectSource = (sourceNode: AudioBufferSourceNode) => {
    activeSources.delete(sourceNode);
    try {
      sourceNode.disconnect();
    } catch {
      // Ignore disconnect failures for already-ended sources.
    }
  };

  const ensureAudioGraph = (): { audioContext: AudioContext | null; gainNode: GainNode | null } => {
    if (audioContext !== null && gainNode !== null) {
      return { audioContext, gainNode };
    }

    const AudioContextCtor = getAudioContextConstructor();
    if (AudioContextCtor === null) {
      return {
        audioContext: null,
        gainNode: null
      };
    }

    try {
      audioContext = new AudioContextCtor();
      gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);
      applyGain(gainNode, audioContext, currentVolume);
    } catch {
      audioContext = null;
      gainNode = null;
    }

    return { audioContext, gainNode };
  };

  const ensureBuffer = (soundEffectId: SoundEffectId): Promise<AudioBuffer | null> => {
    const existingPromise = bufferPromises.get(soundEffectId);
    if (existingPromise !== undefined) {
      return existingPromise;
    }

    const { audioContext: nextAudioContext } = ensureAudioGraph();
    if (nextAudioContext === null) {
      return Promise.resolve(null);
    }

    const bufferPromise = fetch(SOUND_EFFECT_ASSET_URLS[soundEffectId])
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load sound effect: ${soundEffectId}`);
        }

        return response.arrayBuffer();
      })
      .then((arrayBuffer) => nextAudioContext.decodeAudioData(arrayBuffer.slice(0)))
      .catch(() => null);

    bufferPromises.set(soundEffectId, bufferPromise);
    return bufferPromise;
  };

  const resumeAudioContext = async (nextAudioContext: AudioContext): Promise<boolean> => {
    if (nextAudioContext.state !== "suspended") {
      return true;
    }

    try {
      await nextAudioContext.resume();
      return true;
    } catch {
      return false;
    }
  };

  return {
    play: (soundEffectId) => {
      if (currentVolume === 0 || isDocumentHidden()) {
        return;
      }

      const { audioContext: nextAudioContext, gainNode: nextGainNode } = ensureAudioGraph();
      if (nextAudioContext === null || nextGainNode === null) {
        return;
      }

      void (async () => {
        if (!(await resumeAudioContext(nextAudioContext))) {
          return;
        }

        const buffer = await ensureBuffer(soundEffectId);
        if (buffer === null || currentVolume === 0 || isDocumentHidden()) {
          return;
        }

        const sourceNode = nextAudioContext.createBufferSource();
        sourceNode.buffer = buffer;
        sourceNode.connect(nextGainNode);
        sourceNode.onended = () => {
          disconnectSource(sourceNode);
        };
        activeSources.add(sourceNode);

        try {
          sourceNode.start();
        } catch {
          disconnectSource(sourceNode);
        }
      })();
    },
    syncVolume: (volumePercent) => {
      currentVolume = getGainVolume(volumePercent);

      if (audioContext !== null && gainNode !== null) {
        applyGain(gainNode, audioContext, currentVolume);
      }
    },
    dispose: () => {
      activeSources.forEach((sourceNode) => {
        try {
          sourceNode.stop();
        } catch {
          // Ignore stop failures during teardown.
        }

        disconnectSource(sourceNode);
      });
      activeSources.clear();

      gainNode?.disconnect();
      gainNode = null;

      if (audioContext !== null && audioContext.state !== "closed") {
        void audioContext.close().catch(() => {
          // Ignore close failures during teardown.
        });
      }

      audioContext = null;
      bufferPromises.clear();
    }
  };
}
